import { useTranslation } from 'react-i18next';
import { Button, DataTable } from '@airclou/ui';
import { AppWindow, Plus } from 'lucide-react';
import type { WorkspaceApp } from '@/api/workspaces';
import { useAuth } from '../../contexts/AuthContext'
import { canManageOrg } from '../../navigation'
import { RoleUnknownNotice } from '../state/RoleUnknownNotice'

type Props = {
  apps: WorkspaceApp[];
  columns: any[];
  sorting: any;
  onSortingChange: (value: any) => void;
  onAddApp: () => void;
  /** The page's first load. The table keeps its frame and shows a loading row,
   *  and the empty state is held back until the rows have answered — "no apps
   *  yet" must not flash on a workspace that has some. */
  isLoading?: boolean;
};

export function WorkspaceAppsTab({ apps, columns, sorting, onSortingChange, onAddApp, isLoading = false }: Props) {
  const { t } = useTranslation('dashboard');
  // Workspace writes are `TENANT_ROLE_SETS.MANAGE` on the API as of 2026-09-13
  // (role plan §6.4); `canManageOrg` mirrors that set. Reading stays open to any
  // member — only the writes move.
  const { user } = useAuth();
  const canManage = canManageOrg(user?.orgRole ?? undefined);


  return (
    <div>
      <div className="pb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{t('workspaceTabs.applications')}</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('workspaceTabs.appsEnabled')}</p>
          </div>
          {canManage && (
            <Button onClick={onAddApp}>
              <Plus className="w-4 h-4 mr-2" />
              {t('workspaceTabs.addApplication')}
            </Button>
          )}
        </div>
        <RoleUnknownNotice />
      </div>

      <div>
        {apps.length === 0 && !isLoading ? (
          <div className="p-12 text-center">
            <AppWindow className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">{t('workspaceTabs.noAppsYet')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{t('workspaceTabs.noAppsHelp')}</p>
            {canManage && (
              <Button onClick={onAddApp}>
                <Plus className="w-4 h-4 mr-2" />
                {t('workspaceTabs.addApplication')}
              </Button>
            )}
          </div>
        ) : (
          <DataTable data={apps} columns={columns} sorting={sorting} onSortingChange={onSortingChange} isLoading={isLoading && apps.length === 0} />
        )}
      </div>
    </div>
  );
}
